import { fmt } from "../format/number";
import { BAR_DIAMETERS, BAR_SPACINGS, type BarSpec } from "../types/input";
import type { RcCodeProfile, RcMaterials } from "./codes";

/** Tek çubuk alanı (mm²) */
export const barArea = (dia: number): number => (Math.PI * dia * dia) / 4;
/** Çap/aralık → mm²/m */
export const areaPerMetre = (bar: BarSpec): number => barArea(bar.dia) / bar.spacing;

export interface FlexureResult {
  Md: number;                  // kNm/m (tasarım)
  b: number; d: number; h: number;
  /** Gerekli alan (mm²/m), süneklik sınırı aşıldıysa yine de hesaplanır (uyarı ile) */
  AsReq: number;
  a: number; c: number;        // blok derinliği, tarafsız eksen (m)
  cOverD: number; cOverDMax: number; ductile: boolean;
  /** Kesitin sığabileceği en büyük moment (c/d sınırında), kNm/m */
  MdMax: number;
  formula: string;
}

/**
 * Dikdörtgen kesit, tek sıra çekme donatısı, dikdörtgen gerilme bloğu:
 *   M_d/φ = σ_b·b·a·(d − a/2) → a = d − √(d² − 2·M_d/(φ·σ_b·b)) ; A_s = σ_b·b·a / f_yd
 * σ_b: TS 500 0,85·fcd · EC2 η·fcd · ACI 0,85·f'c.  Süneklik: c = a/k₁, c/d ≤ sınır.
 */
export function designFlexure(p: RcCodeProfile, m: RcMaterials, Md: number, b: number, d: number, h: number): FlexureResult {
  const sb = m.sigmaBlock * 1000;   // kN/m²
  const fyd = m.fyd * 1000;
  const cMax = p.maxNeutralAxisRatio(m);
  const aMax = m.k1 * cMax * d;
  const MdMax = m.phiFlexure * sb * b * aMax * (d - aMax / 2);
  const Mn = Math.max(0, Md) / m.phiFlexure;
  const disc = d * d - (2 * Mn) / (sb * b);
  let a: number;
  if (disc >= 0) a = d - Math.sqrt(disc);
  else a = d;                       // kesit yetersiz: blok tüm derinliği kaplar (uyarı)
  const AsReq = ((sb * b * a) / fyd) * 1e6;
  const c = a / m.k1;
  const cOverD = c / d;
  return {
    Md, b, d, h, AsReq, a, c, cOverD, cOverDMax: cMax, ductile: cOverD <= cMax + 1e-9 && disc >= 0, MdMax,
    formula: `a = d − √(d² − 2·M_d/(φ·σ_b·b)); A_s = σ_b·b·a/f_yd; σ_b = ${fmt(m.sigmaBlock, 2)} MPa, f_yd = ${fmt(m.fyd, 1)} MPa, k₁ = ${fmt(m.k1, 2)}`,
  };
}

/** Sağlanan donatı ile moment kapasitesi (kNm/m): M_r = φ·A_s·f_yd·(d − a/2), a = A_s·f_yd/(σ_b·b) */
export function momentCapacity(m: RcMaterials, AsProv: number, b: number, d: number): number {
  const As = AsProv / 1e6;
  const a = (As * m.fyd) / (m.sigmaBlock * b);
  return m.phiFlexure * As * m.fyd * 1000 * (d - a / 2);
}

export interface ShearResult { Vd: number; Vr: number; ratio: number; ok: boolean; formula: string }
export function checkShear(p: RcCodeProfile, m: RcMaterials, Vd: number, b: number, d: number, AsProv: number): ShearResult {
  const rho = AsProv / 1e6 / (b * d);
  const { Vr, formula } = p.shearResistance(m, b, d, rho);
  const V = Math.abs(Vd);
  return { Vd: V, Vr, ratio: Vr > 0 ? V / Vr : Infinity, ok: V <= Vr + 1e-9, formula };
}

export interface BarChoice { dia: number; spacing: number; As: number }
/**
 * En ekonomik çap/aralık: A_s ≥ gereken, aralık ≤ en büyük; en küçük alan, eşitlikte büyük aralık (az çubuk), sonra küçük çap.
 * Hiçbiri yetmezse en büyük çap / en sık aralık döner (ok = false; çağıran uyarır).
 */
export function selectBars(AsNeeded: number, maxSpacing: number, dias: readonly number[] = BAR_DIAMETERS, spacings: readonly number[] = BAR_SPACINGS): BarChoice & { ok: boolean } {
  let best: BarChoice | null = null;
  for (const dia of dias) for (const s of spacings) {
    if (s > maxSpacing + 1e-9) continue;
    const As = barArea(dia) / s;
    if (As + 1e-9 < AsNeeded) continue;
    if (!best || As < best.As - 1e-9 || (Math.abs(As - best.As) < 1e-9 && (s > best.spacing || (s === best.spacing && dia < best.dia)))) best = { dia, spacing: s, As };
  }
  if (best) return { ...best, ok: true };
  const dia = Math.max(...dias), spacing = Math.min(...spacings.filter((s) => s <= maxSpacing + 1e-9), Math.min(...spacings));
  return { dia, spacing, As: barArea(dia) / spacing, ok: false };
}

/** Elle verilen donatı → seçim nesnesi (aralık sınırı denetlenir) */
export function manualBars(bar: BarSpec, AsNeeded: number, maxSpacing: number): BarChoice & { ok: boolean } {
  const As = areaPerMetre(bar);
  return { dia: bar.dia, spacing: bar.spacing, As, ok: As + 1e-9 >= AsNeeded && bar.spacing <= maxSpacing + 1e-9 };
}
